import { type FormEvent, useId, useState } from "react";

import { siteCopy } from "../../content";
import {
  CONTACT_FORM_NAME,
  QUESTIONNAIRE_VERSION,
  contactSubmissionSchema,
  type SoulCheckAnswers,
} from "../../lib/intake-schema";
import "./contact-forms.css";
import { submitNetlifyForm } from "./netlify-forms";

const copy = siteCopy.contact;

type SubmissionStatus = "idle" | "submitting" | "success" | "error";

export type SoulCheckShareContext = {
  answers: SoulCheckAnswers;
  reflection?: string;
};

export type ContactFormProps = {
  soulCheck?: SoulCheckShareContext;
  privacyUrl?: string;
  onSuccess?: () => void;
};

function presentValidationError(): string {
  return "Controleer je e-mailadres en de keuzes hieronder. Je bericht is nog niet verstuurd.";
}

export function ContactForm({
  soulCheck,
  privacyUrl = "/privacy",
  onSuccess,
}: ContactFormProps) {
  const idPrefix = useId();
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [website, setWebsite] = useState("");
  const [contactConsent, setContactConsent] = useState(false);
  const [newsletterConsent, setNewsletterConsent] = useState(false);
  const [shareSoulCheck, setShareSoulCheck] = useState(false);
  const [shareReflection, setShareReflection] = useState(false);
  const [status, setStatus] = useState<SubmissionStatus>("idle");
  const [statusMessage, setStatusMessage] = useState("");

  const reflectionAvailable = Boolean(soulCheck?.reflection?.trim());
  const isSubmitting = status === "submitting";

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const answers = shareSoulCheck ? soulCheck?.answers : undefined;
    const fields = {
      "form-name": CONTACT_FORM_NAME,
      first_name: firstName.trim() || undefined,
      email: email.trim(),
      contact_message: message.trim() || undefined,
      contact_consent: contactConsent ? "yes" : "no",
      newsletter_consent: newsletterConsent ? "yes" : "no",
      share_soul_check: answers ? "yes" : "no",
      questionnaire_version: answers ? QUESTIONNAIRE_VERSION : undefined,
      q1: answers?.q1,
      q2: answers?.q2,
      q3: answers?.q3,
      q4: answers?.q4,
      share_reflection:
        shareReflection && reflectionAvailable ? "yes" : "no",
      reflection:
        shareReflection && reflectionAvailable
          ? soulCheck?.reflection?.trim()
          : undefined,
      website,
    };

    const parsed = contactSubmissionSchema.safeParse(fields);
    if (!parsed.success) {
      setStatus("error");
      setStatusMessage(presentValidationError());
      return;
    }

    setStatus("submitting");
    setStatusMessage("Je bericht wordt verstuurd…");

    try {
      await submitNetlifyForm(fields);
      setFirstName("");
      setEmail("");
      setMessage("");
      setWebsite("");
      setContactConsent(false);
      setNewsletterConsent(false);
      setShareSoulCheck(false);
      setShareReflection(false);
      setStatus("success");
      setStatusMessage(
        newsletterConsent
          ? "Dank je. Je bericht en je aparte nieuwsbriefkeuze zijn ontvangen."
          : "Dank je. Je bericht is ontvangen.",
      );
      onSuccess?.();
    } catch {
      setStatus("error");
      setStatusMessage(
        "Versturen lukte niet. Probeer het straks opnieuw; er is nog niets bevestigd.",
      );
    }
  }

  if (status === "success") {
    return (
      <section className="contact-form contact-form--success" aria-live="polite">
        <h3>{copy.successTitle}</h3>
        <p>{statusMessage}</p>
      </section>
    );
  }

  return (
    <form
      className="contact-form"
      name={CONTACT_FORM_NAME}
      method="post"
      data-netlify="true"
      data-netlify-honeypot="website"
      onSubmit={(event) => {
        void handleSubmit(event);
      }}
      noValidate
    >
      <input type="hidden" name="form-name" value={CONTACT_FORM_NAME} />

      <div className="contact-form__intro">
        <p className="contact-form__eyebrow">{copy.eyebrow}</p>
        <h3>{copy.title}</h3>
        <p>{copy.intro}</p>
      </div>

      <div className="contact-form__field">
        <label htmlFor={`${idPrefix}-first-name`}>Voornaam (optioneel)</label>
        <input
          id={`${idPrefix}-first-name`}
          name="first_name"
          type="text"
          autoComplete="given-name"
          maxLength={80}
          value={firstName}
          onChange={(event) => setFirstName(event.currentTarget.value)}
        />
      </div>

      <div className="contact-form__field">
        <label htmlFor={`${idPrefix}-email`}>E-mailadres</label>
        <input
          id={`${idPrefix}-email`}
          name="email"
          type="email"
          autoComplete="email"
          inputMode="email"
          maxLength={254}
          required
          value={email}
          aria-describedby={`${idPrefix}-email-help`}
          onChange={(event) => setEmail(event.currentTarget.value)}
        />
        <span id={`${idPrefix}-email-help`} className="contact-form__help">
          Nodig om op je bericht te kunnen reageren.
        </span>
      </div>

      <div className="contact-form__field">
        <label htmlFor={`${idPrefix}-message`}>Wat wil je vragen? (optioneel)</label>
        <textarea
          id={`${idPrefix}-message`}
          name="contact_message"
          rows={4}
          maxLength={800}
          value={message}
          onChange={(event) => setMessage(event.currentTarget.value)}
        />
        <span className="contact-form__counter" aria-live="off">
          {message.length}/800
        </span>
      </div>

      {soulCheck ? (
        <fieldset className="contact-form__choices">
          <legend>{copy.shareLegend}</legend>

          <label className="contact-form__check">
            <input
              name="share_soul_check"
              type="checkbox"
              value="yes"
              checked={shareSoulCheck}
              onChange={(event) =>
                setShareSoulCheck(event.currentTarget.checked)
              }
            />
            <span>
              <strong>{copy.shareAnswersTitle}</strong>
              <small>{copy.shareAnswersDetail}</small>
            </span>
          </label>

          {reflectionAvailable ? (
            <label className="contact-form__check">
              <input
                name="share_reflection"
                type="checkbox"
                value="yes"
                checked={shareReflection}
                onChange={(event) =>
                  setShareReflection(event.currentTarget.checked)
                }
              />
              <span>
                <strong>{copy.shareReflectionTitle}</strong>
                <small>{copy.shareReflectionDetail}</small>
              </span>
            </label>
          ) : null}
        </fieldset>
      ) : null}

      <fieldset className="contact-form__choices">
        <legend>{copy.consentLegend}</legend>

        <label className="contact-form__check">
          <input
            name="contact_consent"
            type="checkbox"
            value="yes"
            required
            checked={contactConsent}
            onChange={(event) =>
              setContactConsent(event.currentTarget.checked)
            }
          />
          <span>
            <strong>{copy.contactConsentTitle}</strong>
            <small>{copy.contactConsentDetail}</small>
          </span>
        </label>

        <label className="contact-form__check">
          <input
            name="newsletter_consent"
            type="checkbox"
            value="yes"
            checked={newsletterConsent}
            onChange={(event) =>
              setNewsletterConsent(event.currentTarget.checked)
            }
          />
          <span>
            <strong>{copy.newsletterConsentTitle}</strong>
            <small>{copy.newsletterConsentDetail}</small>
          </span>
        </label>
      </fieldset>

      <p className="contact-form__privacy">
        Lees hoe je gegevens worden gebruikt in de{" "}
        <a href={privacyUrl}>privacyverklaring</a>.
      </p>

      <div
        className="contact-form__honeypot"
        aria-hidden="true"
      >
        <label htmlFor={`${idPrefix}-website`}>Laat dit veld leeg</label>
        <input
          id={`${idPrefix}-website`}
          name="website"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={website}
          onChange={(event) => setWebsite(event.currentTarget.value)}
        />
      </div>

      <button
        className="contact-form__submit"
        type="submit"
        disabled={isSubmitting}
      >
        {isSubmitting ? "Even versturen…" : copy.submitLabel}
      </button>

      <p
        className={`contact-form__status contact-form__status--${status}`}
        role={status === "error" ? "alert" : "status"}
        aria-live={status === "error" ? "assertive" : "polite"}
      >
        {statusMessage}
      </p>
    </form>
  );
}
